import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronDownCircle } from "lucide-react";

import messages from "../../assets/locale/messages";
import { groupNotificationsByDate } from "../../utils/notificationsGrouping";
import { handleNotificationsClickActions } from "../../firebase/helpers";
import { handleCompanyPortalNotificationClick } from "../../utils/companyPortalNotifications";
import { handleClientPortalNotificationClick } from "../../utils/clientPortalNotifications";
import { getSessionPortal } from "../../utils/Auth";
import { AUTH_CONTEXT_TYPE } from "../../utils/authPortal";
import NotificationItem from "../../components/Notifications/NotificationItem";
import NotificationsEmpty from "../../components/Notifications/NotificationsEmpty";
import {
  getNotificationsRequest,
  getNotificationsResponse,
} from "../../store/Notifications/actions";
import {
  getCompanyNotificationsRequest,
  getCompanyNotificationsResponse,
} from "../../store/CompanyPortal/Notifications/actions";
import {
  getClientNotificationsRequest,
  getClientNotificationsResponse,
} from "../../store/ClientPortal/Notifications/actions";
import Button from "../../components/Button";
import Breadcrumbs from "../../components/BreadCrumbs";
import "./Notifications.scss";

const Notifications = () => {
  const dispatch = useDispatch();

  const isCompanyPortal = getSessionPortal() === AUTH_CONTEXT_TYPE.COMPANY;
  const isClientPortal = getSessionPortal() === AUTH_CONTEXT_TYPE.CLIENT;

  const lang = useSelector((state) => state.locale.lang);
  const adminList = useSelector((state) => state.notifications.list);
  const adminMeta = useSelector((state) => state.notifications.meta);
  const companyList = useSelector((state) => state.companyNotifications.list);
  const companyMeta = useSelector((state) => state.companyNotifications.meta);
  const clientList = useSelector((state) => state.clientNotifications.list);
  const clientMeta = useSelector((state) => state.clientNotifications.meta);
  const notificationsList = isCompanyPortal
    ? companyList
    : isClientPortal
    ? clientList
    : adminList;
  const notificationsMeta = isCompanyPortal
    ? companyMeta
    : isClientPortal
    ? clientMeta
    : adminMeta;
  const { notifications } = messages[lang];

  const [page, setPage] = useState(notificationsMeta?.currentPage || 1);

  const groupedNotifications = useMemo(
    () => groupNotificationsByDate(notificationsList || []),
    [notificationsList],
  );

  /** Derive the unread count from the loaded list — the redux badge count is unreliable here. */
  const unreadCount = useMemo(
    () => (notificationsList || []).filter((item) => !item.is_seen).length,
    [notificationsList],
  );

  const unreadLabel =
    unreadCount > 0
      ? notifications.unreadCount.replace("{count}", unreadCount)
      : notificationsList?.length
        ? notifications.allRead
        : notifications.unreadCount.replace("{count}", 0);

  const getGroupLabel = (group) =>
    group.bucket === "date" ? group.dateLabel : notifications[group.bucket];

  const fetchNotifications = (pageNumber) => {
    const params = { items: 10, page: pageNumber };
    dispatch(
      isCompanyPortal
        ? getCompanyNotificationsRequest(params)
        : isClientPortal
        ? getClientNotificationsRequest(params)
        : getNotificationsRequest(params),
    );
  };

  useEffect(() => {
    fetchNotifications(page);
  }, [page]);

  useEffect(() => {
    fetchNotifications(1);
  }, [lang]);

  useEffect(() => {
    return () => {
      const clearPayload = { list: [], meta: {}, clear: true };
      dispatch(
        isCompanyPortal
          ? getCompanyNotificationsResponse(clearPayload)
          : isClientPortal
          ? getClientNotificationsResponse(clearPayload)
          : getNotificationsResponse(clearPayload),
      );
    };
  }, []);

  const handleNotificationClick = (notification) => {
    if (isCompanyPortal) handleCompanyPortalNotificationClick(notification);
    else if (isClientPortal) handleClientPortalNotificationClick(notification);
    else handleNotificationsClickActions(notification);
  };

  const hasMore =
    notificationsMeta?.count &&
    notificationsMeta?.pages > 1 &&
    page < notificationsMeta?.pages;

  return (
    <div className="notifications-page">
      <div className="page-title-margin">
        <Breadcrumbs
          list={[{ path: "", label: notifications.title }]}
          BreadCrumbHasBackIcon={false}
        />
      </div>
      <div className="notifications-page__header">
        <span className="notifications-page__title">
          {notifications.allNotifications}
        </span>
        <span className="notifications-page__count">{unreadLabel}</span>
      </div>
      <div className="content-wrapper">
        {!notificationsList?.length ? (
          <NotificationsEmpty
            title={notifications.allCaughtUp}
            description={notifications.emptyDescription}
          />
        ) : (
          <>
            <div className="notifications-page__groups">
              {groupedNotifications.map((group) => (
                <div className="notifications-page__group" key={group.key}>
                  <div className="notifications-page__group-label">
                    {getGroupLabel(group)}
                  </div>
                  <div className="notifications-page__list">
                    {group.items.map(
                      ({
                        id,
                        title,
                        body,
                        created_at,
                        is_seen,
                        click_action,
                        action_id,
                        event_id,
                      }) => (
                        <NotificationItem
                          key={id}
                          variant="page"
                          title={title}
                          body={body}
                          createdAt={created_at}
                          isSeen={is_seen}
                          onClick={() =>
                            handleNotificationClick({
                              id,
                              click_action,
                              action_id,
                              event_id,
                              is_seen,
                            })
                          }
                        />
                      ),
                    )}
                  </div>
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="notifications-page__load-more-wrapper">
                <Button
                  outlined
                  className="notifications-page__load-more"
                  onClick={() => setPage(page + 1)}
                  label={
                    <span className="notifications-page__load-more-label">
                      <ChevronDownCircle size={18} strokeWidth={1.75} />
                      {notifications.loadMore}
                    </span>
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Notifications;
